<template>
  <modal-view
    :title="'Request Inventory Transfer'"
    :custom-max-width="'1100px'"
    :custom-content-height="'85vh'"
    custom-class="ireq-modal"
    custom-class-content="ireq-body"
    :is-nested="true"
    @close-modal="$emit('close')"
  >
    <div class="ireq-flow">

      <!-- ── Controls bar ───────────────────────────────────────── -->
      <div class="ireq-hero">
        <div class="ireq-steps">

          <!-- Step 1: Group -->
          <div class="ireq-step">
            <span class="ireq-step-label">
              <vue-feather type="users" size="13" />
              Step 1 — Group
              <span class="ireq-required">*</span>
            </span>
            <div class="ireq-row">
              <div class="ireq-select-wrap">
                <HSCustomSelect
                  v-model="selectedGroupId"
                  :options="groupSelectOptions"
                  icon="users"
                  :disabled="loadingGroups"
                  class="ireq-hs-select"
                />
              </div>
              <button class="ireq-icon-btn" :disabled="loadingGroups" @click="loadGroups" title="Refresh groups">
                <vue-feather type="refresh-ccw" size="15" :class="{ 'ireq-spin': loadingGroups }" />
              </button>
            </div>
            <p v-if="!loadingGroups && groups.length === 0" class="ireq-hint ireq-hint--warn">
              <vue-feather type="alert-circle" size="12" />
              No groups yet — create or join a group first.
            </p>
          </div>

          <!-- Step 2: Store (revealed after group) -->
          <transition name="ireq-fade">
            <div v-if="selectedGroupId" class="ireq-step">
              <span class="ireq-step-label">
                <vue-feather type="home" size="13" />
                Step 2 — Request From
                <span class="ireq-required">*</span>
              </span>
              <div class="ireq-row">
                <div class="ireq-select-wrap">
                  <HSCustomSelect
                    v-model="selectedTargetStoreId"
                    :options="targetStoreSelectOptions"
                    icon="home"
                    :disabled="loadingStores"
                    class="ireq-hs-select"
                  />
                </div>
                <button class="ireq-icon-btn" :disabled="loadingStores" @click="loadGroupStores(selectedGroupId)" title="Refresh stores">
                  <vue-feather type="refresh-ccw" size="15" :class="{ 'ireq-spin': loadingStores }" />
                </button>
              </div>
              <p v-if="!loadingStores && storesLoadError" class="ireq-hint ireq-hint--error">
                <vue-feather type="alert-circle" size="12" />
                {{ storesLoadError }}
              </p>
              <p v-else-if="!loadingStores && targetStores.length === 0" class="ireq-hint ireq-hint--warn">
                <vue-feather type="alert-circle" size="12" />
                No other stores in this group.
              </p>
            </div>
          </transition>

        </div>
      </div>

      <!-- ── Content ────────────────────────────────────────────── -->
      <div class="ireq-content">

        <!-- Loading -->
        <div v-if="loading" class="ireq-empty">
          <span class="ireq-spinner ireq-spinner--lg"></span>
          <p>Loading…</p>
        </div>

        <!-- Prompt to select -->
        <div v-else-if="!selectedGroupId || !selectedTargetStoreId" class="ireq-empty">
          <div class="ireq-empty-icon">
            <vue-feather type="arrow-up-circle" size="32" />
          </div>
          <p>Select a group and target store above to begin</p>
        </div>

        <!-- Items -->
        <div v-else class="ireq-items">
          <div
            v-for="item in requestItems"
            :key="item.product.id"
            class="ireq-item-card"
          >
            <!-- Product identity -->
            <div class="ireq-item-info">
              <div class="ireq-item-icon">
                <vue-feather type="package" size="16" />
              </div>
              <div class="ireq-item-meta">
                <span class="ireq-item-name">{{ item.product.name }}</span>
                <div class="ireq-item-tags">
                  <span class="ireq-tag ireq-tag--sku">{{ item.product.sku || 'No SKU' }}</span>
                  <span class="ireq-tag ireq-tag--unit">
                    {{ item.requestUnitContextLabel }}
                  </span>
                  <span class="ireq-tag" :class="item.productMappingId ? 'ireq-tag--mapped' : 'ireq-tag--unmapped'">
                    {{ item.unitContextLabel }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Quantity + unit -->
            <div class="ireq-item-qty">
              <span class="ireq-qty-label">Qty needed</span>
              <div class="ireq-qty-row">
                <div class="ireq-field-control ireq-qty-input-wrap">
                  <input
                    v-model.number="item.quantity"
                    type="number"
                    min="0.01"
                    step="0.01"
                    class="ireq-qty-input"
                    placeholder="0"
                  />
                </div>
                <div class="ireq-field-control ireq-unit-wrap">
                  <select
                    v-model="item.quantityUnitOptionKey"
                    :disabled="item.quantityUnitLoading"
                    class="ireq-unit-select"
                    @change="handleUnitChange(item)"
                  >
                    <option v-for="unit in item.quantityUnitOptions" :key="unit.optionKey" :value="unit.optionKey">
                      {{ unit.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="ireq-unit-conversion" :class="{ 'ireq-unit-conversion--packaging': quantityConversion(item).usesPackaging }">
                <div class="ireq-unit-conversion__line">
                  <vue-feather type="send" size="12" />
                  <span>
                    Sending {{ quantityConversion(item).selectedQuantity }}
                    {{ quantityConversion(item).selectedUnitLabel }}
                  </span>
                </div>
                <div class="ireq-unit-conversion__line">
                  <vue-feather type="repeat" size="12" />
                  <span>
                    {{ quantityConversion(item).conversionLabel }}
                  </span>
                </div>
              </div>
              <div
                v-if="transferInventorySummary(item)"
                class="ireq-unit-conversion ireq-unit-conversion--inventory"
              >
                <div class="ireq-unit-conversion__line">
                  <vue-feather type="box" size="12" />
                  <span>Available {{ transferInventorySummary(item).available }}</span>
                </div>
                <div class="ireq-unit-conversion__line">
                  <vue-feather type="corner-down-right" size="12" />
                  <span>After sending: {{ transferInventorySummary(item).remaining }}</span>
                </div>
                <div
                  v-if="transferInventorySummary(item).usesPackaging"
                  class="ireq-unit-conversion__line"
                >
                  <vue-feather type="hash" size="12" />
                  <span>{{ transferInventorySummary(item).formula }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty items -->
          <div v-if="requestItems.length === 0" class="ireq-empty">
            <div class="ireq-empty-icon">
              <vue-feather type="inbox" size="32" />
            </div>
            <p>No products selected</p>
          </div>

          <!-- Note -->
          <div v-if="selectedTargetStoreId && requestItems.length > 0" class="ireq-note-section">
            <span class="ireq-step-label">
              <vue-feather type="file-text" size="13" />
              Note
              <span class="ireq-optional">(optional)</span>
            </span>
            <div class="ireq-field-control ireq-field-control--textarea">
              <vue-feather type="align-left" size="15" />
              <textarea
                v-model="note"
                rows="3"
                class="ireq-note"
                placeholder="e.g., Monthly restock, needed for customer order…"
              ></textarea>
            </div>
            <p class="ireq-hint">
              <vue-feather type="info" size="12" />
              Visible to the fulfilling store
            </p>
          </div>
        </div>
      </div>

      <!-- ── Footer ─────────────────────────────────────────────── -->
      <div class="ireq-footer">
        <div v-if="formError" class="ireq-error-banner">
          <vue-feather type="alert-circle" size="14" />
          {{ formError }}
        </div>
        <div class="ireq-footer-row">
          <span class="ireq-counter">
            <vue-feather type="check-circle" size="13" />
            {{ validItemsCount }} / {{ requestItems.length }} items ready
          </span>
          <div class="ireq-actions">
            <button type="button" class="ireq-btn ireq-btn--cancel" @click="$emit('close')">
              Cancel
            </button>
            <button
              type="button"
              class="ireq-btn ireq-btn--submit"
              :disabled="saving || !canSubmit"
              @click="showConfirmation = true"
            >
              <vue-feather v-if="!saving" type="send" size="15" />
              <span v-else class="ireq-btn-spinner"></span>
              {{ saving ? 'Sending…' : 'Create Request' }}
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- ── Confirmation dialog (z above modal z-5400) ─────────── -->
    <teleport to="body">
      <transition name="ireq-fade">
        <div
          v-if="showConfirmation"
          class="ireq-confirm-overlay"
          @click.self="showConfirmation = false"
        >
          <div class="ireq-confirm-panel">
            <div class="ireq-confirm-icon">
              <vue-feather type="send" size="22" />
            </div>
            <div class="ireq-confirm-body">
              <p class="ireq-confirm-eyebrow">Confirm transfer request</p>
              <h3 class="ireq-confirm-title">Create Transfer Request</h3>
              <p class="ireq-confirm-desc">
                You are requesting <strong>{{ validItemsCount }} product(s)</strong> from
                <strong>{{ targetStoreName }}</strong>.
              </p>
              <div class="ireq-confirm-notice">
                <vue-feather type="info" size="13" />
                <span>{{ targetStoreName }} must approve and fulfill before inventory is transferred.</span>
              </div>
              <div class="ireq-confirm-items">
                <div
                  v-for="item in requestItems.filter((entry) => Number(entry.quantity) > 0)"
                  :key="item.product.id"
                  class="ireq-confirm-item"
                >
                  <span>{{ item.product.name }}</span>
                  <strong>{{ transferQuantitySummary(item) }}</strong>
                </div>
              </div>
              <div class="ireq-note-section ireq-note-section--compact">
                <span class="ireq-step-label">
                  <vue-feather type="file-text" size="12" />
                  Request Note
                </span>
                <div class="ireq-field-control ireq-field-control--textarea">
                  <vue-feather type="align-left" size="14" />
                  <textarea
                    v-model="note"
                    rows="2"
                    class="ireq-note"
                    placeholder="Add a note for the fulfilling store…"
                  ></textarea>
                </div>
              </div>
            </div>
            <div class="ireq-confirm-footer">
              <button type="button" class="ireq-btn ireq-btn--cancel" :disabled="saving" @click="showConfirmation = false">
                Cancel
              </button>
              <button type="button" class="ireq-btn ireq-btn--submit" :disabled="saving" @click="handleConfirmedSubmit">
                <span v-if="saving" class="ireq-btn-spinner"></span>
                <vue-feather v-else type="check" size="15" />
                {{ saving ? 'Processing…' : 'Yes, Create Request' }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </modal-view>
</template>

<script>
import { computed, ref, watch, reactive } from "vue";
import { useStore } from "vuex";
import ModalView from "@/components/modals/ModalView.vue";
import HSCustomSelect from "@/components/common/HSCustomSelect.vue";
import http from "@/helper/http";
import { toast } from "vue3-toastify";

export default {
  name: "TransferRequestModal",
  components: {
    ModalView,
    HSCustomSelect,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    targetStoreId: {
      type: Number,
      required: true,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "submitted"],
  setup(props, { emit }) {
    const store = useStore();
    const groups = ref([]);
    const selectedGroupId = ref("");
    const selectedTargetStoreId = ref("");
    const note = ref("");
    const targetStores = ref([]);
    const loading = ref(false);
    const loadingGroups = ref(false);
    const loadingStores = ref(false);
    const storesLoadError = ref("");
    const saving = ref(false);
    const formError = ref("");
    const showConfirmation = ref(false);
    const fulfillerInventoryMap = ref(new Map());

    const requestItems = reactive([]);

    const targetStoreName = computed(() => {
      const store = targetStores.value.find(
        (s) => s.store_id === selectedTargetStoreId.value,
      );
      return store?.store_name || "target store";
    });

    const groupSelectOptions = computed(() => [
      {
        value: "",
        label: loadingGroups.value
          ? "Loading groups..."
          : groups.value.length === 0
            ? "No groups available"
            : "Select group...",
      },
      ...groups.value.map((group) => ({
        value: group.id,
        label: group.name,
      })),
    ]);

    const targetStoreSelectOptions = computed(() => [
      {
        value: "",
        label: loadingStores.value
          ? "Loading stores..."
          : storesLoadError.value
            ? "Unable to load group stores"
            : targetStores.value.length === 0
              ? "No other stores in group"
              : "Select target store...",
      },
      ...targetStores.value.map((store) => ({
        value: store.store_id,
        label: store.store_name,
      })),
    ]);

    const validItemsCount = computed(() => {
      return requestItems.filter((item) => Number(item.quantity) > 0).length;
    });

    const canSubmit = computed(() => {
      if (!selectedGroupId.value || !selectedTargetStoreId.value) return false;
      if (requestItems.length === 0) return false;
      return requestItems.every((item) => Number(item.quantity) > 0);
    });

    const resetState = () => {
      groups.value = [];
      selectedGroupId.value = "";
      selectedTargetStoreId.value = "";
      note.value = "";
      targetStores.value = [];
      storesLoadError.value = "";
      requestItems.splice(0, requestItems.length);
      formError.value = "";
      showConfirmation.value = false;
    };

    const loadGroups = async () => {
      console.log("🔄 Loading groups...");
      loadingGroups.value = true;
      try {
        const response = await http.get(
          `/v2/stores/${props.targetStoreId}/groups`,
          {
            params: {
              page: 1,
              per_page: 100,
              _ts: Date.now(),
            },
          },
        );
        groups.value = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];

        console.log(`✅ Loaded ${groups.value.length} groups`);

        // Auto-select default group or first group if none selected
        if (groups.value.length > 0 && !selectedGroupId.value) {
          // Get current store's default group
          const currentStore = store.getters.getUserStores?.find(
            (s) => s.id === props.targetStoreId,
          );
          const defaultGroupId = currentStore?.default_group_id;

          // Select default group if set and exists in list, otherwise first group
          if (
            defaultGroupId &&
            groups.value.some((g) => g.id === defaultGroupId)
          ) {
            selectedGroupId.value = defaultGroupId;
            console.log(`✅ Auto-selected default group: ${defaultGroupId}`);
          } else {
            selectedGroupId.value = groups.value[0].id;
            console.log(`✅ Auto-selected first group: ${groups.value[0].id}`);
          }
        }
      } catch (error) {
        console.error("❌ Failed to load groups", error);
        toast.error("Unable to load groups. Please try again.");
      } finally {
        loadingGroups.value = false;
      }
    };

    const loadGroupStores = async (groupId) => {
      if (!groupId) {
        console.warn("⚠️ No group ID provided to loadGroupStores");
        return;
      }

      console.log(`🔄 Loading stores for group ${groupId}...`);
      loadingStores.value = true;
      storesLoadError.value = "";
      try {
        const response = await http.get(`/v2/groups/${groupId}/stores`, {
          params: {
            per_page: 100,
            _ts: Date.now(),
          },
        });
        const stores = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];

        console.log("📦 Raw stores data:", stores);

        const currentStoreId = String(props.targetStoreId);

        // Filter out current requester store. "Request From" should only list
        // other stores in the same group that can fulfill the transfer.
        targetStores.value = stores
          .filter((s) => String(s.store_id) !== currentStoreId)
          .map((s) => ({
            store_id: s.store_id,
            store_name: s.name,
            merchant_code: s.merchant_code,
          }));

        console.log(
          `✅ Loaded ${targetStores.value.length} fulfiller stores (${stores.length} total, excluding current requester store)`,
          targetStores.value,
        );

        // Auto-select first target store by default
        if (targetStores.value.length > 0) {
          selectedTargetStoreId.value = targetStores.value[0].store_id;
        }
      } catch (error) {
        console.error("❌ Failed to load group stores", error);
        targetStores.value = [];
        selectedTargetStoreId.value = "";
        storesLoadError.value =
          error?.response?.data?.error?.message ||
          "Unable to load stores for this group. Check your access and try again.";
        toast.error(storesLoadError.value);
      } finally {
        loadingStores.value = false;
      }
    };

    const rebuildRequestItems = () => {
      requestItems.splice(0, requestItems.length);

      props.selectedItems.forEach((item) => {
        const product = item.product || item.originalData?.product || {};
        const quantityUnit =
          item.quantity_unit ||
          item.default_quantity_unit ||
          product.quantity_unit ||
          product.default_quantity_unit ||
          "U";

        const fallbackUnitOption = buildFallbackUnitOption(
          quantityUnit,
          props.targetStoreId,
          item.id,
        );

        const requestItem = {
          product: {
            id: item.id,
            name: item.name,
            sku: item.sku || item.item_sku,
            quantity_unit: product.quantity_unit || item.quantity_unit,
            default_quantity_unit:
              product.default_quantity_unit || item.default_quantity_unit,
          },
          quantityUnit: quantityUnit,
          quantityUnitLabel: quantityUnit,
          quantityUnitOptionKey: fallbackUnitOption.optionKey,
          quantityUnitRef: fallbackUnitOption.ref,
          quantityUnitOptions: [fallbackUnitOption],
          quantityUnitLoading: false,
          productMappingId: null,
          fulfillerProductId: null,
          fulfillerProductUnit: "",
          fulfillerUnitOptions: [],
          unitOwnerStoreId: props.targetStoreId,
          unitOwnerProductId: item.id,
          unitContextLabel: "Not mapped",
          requestUnitContextLabel: `Request in ${unitCode(quantityUnit)}`,
          quantity: 1,
        };

        requestItems.push(requestItem);
      });

      refreshMappingAwareUnitOptions();
    };

    const unitCode = (value) => String(value || "U").trim().toUpperCase();

    const unitDisplayLabel = (unit) => {
      const code = unitCode(unit?.unit || unit?.code || unit?.unit_code);
      const serverLabel = String(unit?.display_label || "").trim();
      if (serverLabel) return serverLabel;
      const label = String(unit?.name || unit?.label || code).trim();
      const scope = unit?.scope || "system";
      const factor = unit?.conversion_factor_to_base || unit?.factor;
      const base = unit?.base_unit || unit?.parent_unit;
      if (
        ["store_custom", "product_packaging"].includes(scope) &&
        factor &&
        base
      ) {
        const numberFactor = Number(factor);
        const factorLabel = Number.isFinite(numberFactor)
          ? numberFactor % 1 === 0
            ? String(numberFactor)
            : String(factor)
          : String(factor);
        return `${label} (${code}, ${factorLabel} ${unitCode(base)})`;
      }
      return label && label.toUpperCase() !== code ? `${label} (${code})` : code;
    };

    const formatQuantity = (value) => {
      const numberValue = Number(value || 0);
      if (!Number.isFinite(numberValue)) return "0";
      return numberValue.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    };

    const buildFallbackUnitRef = (code, storeId, productId) => ({
      scope: "legacy",
      id: null,
      store_id: storeId || null,
      product_id: productId || null,
      code: unitCode(code),
      label: unitCode(code),
    });

    const normalizeUnitOption = (unit, ownerStoreId, ownerProductId) => {
      const code = unitCode(unit?.unit || unit?.code || unit?.unit_code);
      const scope = unit?.scope || "system";
      const label = unitDisplayLabel(unit);
      const factor = unit?.conversion_factor_to_base || unit?.factor || "";
      const baseUnit = unit?.base_unit || unit?.parent_unit || "";
      return {
        optionKey: `${scope}:${unit?.id || code}:${ownerStoreId}:${ownerProductId}:${baseUnit}:${factor}`,
        code,
        label,
        factor: Number(factor || 1),
        baseUnit: unitCode(baseUnit || code),
        scope,
        ref: {
          scope,
          id: unit?.id || null,
          store_id: unit?.store_id || ownerStoreId || null,
          product_id:
            unit?.product_id ||
            (scope === "product_packaging" ? ownerProductId : null),
          code,
          label: unit?.label || unit?.name || code,
          base_unit: unitCode(baseUnit || code),
          conversion_factor_to_base: Number(factor || 1),
        },
      };
    };

    const buildFallbackUnitOption = (code, ownerStoreId, ownerProductId) => ({
      optionKey: `legacy:${unitCode(code)}:${ownerStoreId}:${ownerProductId}`,
      code: unitCode(code),
      label: unitCode(code),
      factor: 1,
      baseUnit: unitCode(code),
      scope: "legacy",
      ref: buildFallbackUnitRef(code, ownerStoreId, ownerProductId),
    });

    const selectedUnitOption = (item) => {
      return item.quantityUnitOptions.find(
        (unit) => unit.optionKey === item.quantityUnitOptionKey,
      ) || item.quantityUnitOptions.find(
        (unit) => unit.code === unitCode(item.quantityUnit),
      ) || buildFallbackUnitOption(
        item.quantityUnit,
        item.unitOwnerStoreId,
        item.unitOwnerProductId,
      );
    };

    const unitOptionByCode = (item, code) => {
      const normalizedCode = unitCode(code);
      const options = item.fulfillerUnitOptions?.length
        ? item.fulfillerUnitOptions
        : item.quantityUnitOptions;
      return options.find((unit) => unit.code === normalizedCode) ||
        buildFallbackUnitOption(
          normalizedCode,
          selectedTargetStoreId.value || item.unitOwnerStoreId,
          item.fulfillerProductId || item.unitOwnerProductId,
        );
    };

    const quantityConversion = (item) => {
      const unit = selectedUnitOption(item);
      const quantity = Number(item.quantity || 0);
      const factor = Number(unit.factor || 1);
      const selectedUnit = unitCode(unit.code || item.quantityUnit);
      const baseUnit = unitCode(unit.baseUnit || selectedUnit);
      const baseQuantity = quantity * (Number.isFinite(factor) ? factor : 1);
      const usesPackaging =
        baseUnit !== selectedUnit ||
        (Number.isFinite(factor) && Math.abs(factor - 1) > 0.000001);
      const selectedQuantity = formatQuantity(quantity);
      const baseQuantityLabel = formatQuantity(baseQuantity);
      const selectedUnitLabel = unit.label || selectedUnit;

      return {
        selectedQuantity,
        selectedUnit,
        selectedUnitLabel,
        baseUnit,
        baseQuantity,
        baseQuantityLabel,
        factor,
        usesPackaging,
        conversionLabel: usesPackaging
          ? `${selectedQuantity} ${selectedUnit} = ${baseQuantityLabel} ${baseUnit}`
          : `${selectedQuantity} ${selectedUnit} stays ${baseQuantityLabel} ${baseUnit}`,
      };
    };

    const transferQuantitySummary = (item) => {
      const conversion = quantityConversion(item);
      return conversion.usesPackaging
        ? conversion.conversionLabel
        : `${conversion.selectedQuantity} ${conversion.selectedUnit}`;
    };

    const transferInventorySummary = (item) => {
      const inventory = fulfillerInventoryMap.value.get(item.fulfillerProductId);
      if (!inventory || !item.productMappingId) return null;

      const inventoryUnit = unitCode(inventory.quantity_unit || item.quantityUnit);
      const inventoryUnitOption = unitOptionByCode(item, inventoryUnit);
      const inventoryFactor = Number(inventoryUnitOption.factor || 1);
      const inventoryBaseUnit = unitCode(inventoryUnitOption.baseUnit || inventoryUnit);
      const inventoryQuantity = Number(inventory.quantity || 0);
      const availableBase = inventoryQuantity * inventoryFactor;
      const requestedBase = quantityConversion(item).baseQuantity;
      const remainingBase = availableBase - requestedBase;
      const remainingPackages = inventoryFactor > 0
        ? remainingBase / inventoryFactor
        : remainingBase;
      const usesPackaging =
        inventoryFactor > 1 || inventoryUnit !== inventoryBaseUnit;
      const inventoryLabel = inventoryUnitOption.label || inventoryUnit;

      return {
        available: usesPackaging
          ? `${formatQuantity(inventoryQuantity)} ${inventoryLabel} = ${formatQuantity(availableBase)} ${inventoryBaseUnit}`
          : `${formatQuantity(inventoryQuantity)} ${inventoryLabel}`,
        remaining: usesPackaging
          ? `${formatQuantity(remainingBase)} ${inventoryBaseUnit} (${formatQuantity(remainingPackages)} ${inventoryLabel})`
          : `${formatQuantity(remainingBase)} ${inventoryBaseUnit}`,
        usesPackaging,
        formula: usesPackaging
          ? `${formatQuantity(inventoryQuantity)} x ${formatQuantity(inventoryFactor)} ${inventoryBaseUnit}/${inventoryLabel} - ${formatQuantity(requestedBase)} ${inventoryBaseUnit} = ${formatQuantity(remainingBase)} ${inventoryBaseUnit}`
          : "",
      };
    };

    const syncSelectedUnitRef = (item) => {
      const unit = selectedUnitOption(item);
      item.quantityUnitOptionKey = unit.optionKey;
      item.quantityUnit = unit.code;
      item.quantityUnitLabel = unit.label;
      item.quantityUnitRef = unit.ref;
    };

    const handleUnitChange = (item) => {
      syncSelectedUnitRef(item);
    };

    const loadUnitOptionsForItem = async (item) => {
      const ownerStoreId = item.unitOwnerStoreId || props.targetStoreId;
      const ownerProductId = item.unitOwnerProductId || item.product.id;
      item.quantityUnitLoading = true;
      try {
        const response = await http.get("/v1/store/inventory/quantity/units", {
          params: {
            store_id: ownerStoreId,
            product_id: ownerProductId,
          },
        });
        const units = Array.isArray(response?.data) ? response.data : [];
        const options = units.map((unit) =>
          normalizeUnitOption(unit, ownerStoreId, ownerProductId),
        );
        if (!options.some((unit) => unit.code === unitCode(item.quantityUnit))) {
          options.unshift(
            buildFallbackUnitOption(
              item.quantityUnit,
              ownerStoreId,
              ownerProductId,
            ),
          );
        }
        item.quantityUnitOptions = options.length
          ? options
          : [buildFallbackUnitOption(item.quantityUnit, ownerStoreId, ownerProductId)];
      } catch (error) {
        console.warn("Unable to load quantity units for transfer item", error);
        item.quantityUnitOptions = [
          buildFallbackUnitOption(item.quantityUnit, ownerStoreId, ownerProductId),
        ];
      } finally {
        syncSelectedUnitRef(item);
        item.quantityUnitLoading = false;
      }
    };

    const loadFulfillerUnitOptionsForItem = async (item) => {
      if (!selectedTargetStoreId.value || !item.fulfillerProductId) {
        item.fulfillerUnitOptions = [];
        return;
      }
      try {
        const response = await http.get("/v1/store/inventory/quantity/units", {
          params: {
            store_id: selectedTargetStoreId.value,
            product_id: item.fulfillerProductId,
          },
        });
        const units = Array.isArray(response?.data) ? response.data : [];
        item.fulfillerUnitOptions = units.map((unit) =>
          normalizeUnitOption(unit, selectedTargetStoreId.value, item.fulfillerProductId),
        );
        if (
          item.fulfillerProductUnit &&
          !item.fulfillerUnitOptions.some(
            (unit) => unit.code === unitCode(item.fulfillerProductUnit),
          )
        ) {
          item.fulfillerUnitOptions.unshift(
            buildFallbackUnitOption(
              item.fulfillerProductUnit,
              selectedTargetStoreId.value,
              item.fulfillerProductId,
            ),
          );
        }
      } catch (error) {
        console.warn("Unable to load fulfiller quantity units", error);
        item.fulfillerUnitOptions = item.fulfillerProductUnit
          ? [
              buildFallbackUnitOption(
                item.fulfillerProductUnit,
                selectedTargetStoreId.value,
                item.fulfillerProductId,
              ),
            ]
          : [];
      }
    };

    const refreshMappingAwareUnitOptions = async () => {
      if (!selectedTargetStoreId.value || requestItems.length === 0) {
        await Promise.all(requestItems.map((item) => loadUnitOptionsForItem(item)));
        return;
      }

      const productIds = requestItems.map((item) => item.product.id).filter(Boolean);
      const mappingsByProductId = new Map();
      try {
        const response = await http.get(
          `/v2/store/${props.targetStoreId}/product-mappings`,
          {
            params: {
              other_store_id: selectedTargetStoreId.value,
              product_ids: productIds.join(","),
              is_active: true,
              per_page: productIds.length || 1,
            },
          },
        );
        const mappings = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        mappings.forEach((mapping) => {
          if (mapping?.product?.id) {
            mappingsByProductId.set(mapping.product.id, mapping);
          }
        });
      } catch (error) {
        console.warn("Unable to load product mappings for transfer units", error);
      }

      await Promise.all(
        requestItems.map(async (item) => {
          const mapping = mappingsByProductId.get(item.product.id);
          if (mapping?.id && mapping?.other_product?.id) {
            item.productMappingId = mapping.id;
            item.fulfillerProductId = mapping.other_product.id;
            item.fulfillerProductUnit = unitCode(
              mapping.other_product.default_quantity_unit ||
                mapping.other_product.quantity_unit ||
                "U",
            );
            item.unitContextLabel = `Fulfiller stock: ${item.fulfillerProductUnit}`;
          } else {
            item.productMappingId = null;
            item.fulfillerProductId = null;
            item.fulfillerProductUnit = "";
            item.fulfillerUnitOptions = [];
            item.unitOwnerStoreId = props.targetStoreId;
            item.unitOwnerProductId = item.product.id;
            item.unitContextLabel = "Not mapped";
          }
          await Promise.all([
            loadUnitOptionsForItem(item),
            loadFulfillerUnitOptionsForItem(item),
          ]);
        }),
      );

      await loadFulfillerInventory();
    };

    const loadFulfillerInventory = async () => {
      const productIds = requestItems
        .filter((item) => item.productMappingId && item.fulfillerProductId)
        .map((item) => item.fulfillerProductId);
      if (!selectedTargetStoreId.value || productIds.length === 0) {
        fulfillerInventoryMap.value = new Map();
        return;
      }
      try {
        const inventoryData = await Promise.all(
          [...new Set(productIds)].map((productId) =>
            fetchInventoryProductDetails(selectedTargetStoreId.value, productId),
          ),
        );
        const nextMap = new Map();
        inventoryData.filter(Boolean).forEach((inventory) => {
          nextMap.set(inventory.product_id, normalizeInventoryProductDetails(inventory));
        });
        fulfillerInventoryMap.value = nextMap;
      } catch (error) {
        console.warn("Unable to load fulfiller inventory for transfer", error);
        fulfillerInventoryMap.value = new Map();
      }
    };

    const fetchInventoryProductDetails = async (storeId, productId) => {
      const response = await http.get(`/v2/store/inventory/${storeId}`, {
        params: {
          "id[eq]": productId,
          include: "has_rra_ebm_metadata",
          page: 1,
          per_page: 1,
        },
      });
      const items = Array.isArray(response?.data?.items)
        ? response.data.items
        : [];
      return items[0] || null;
    };

    const normalizeInventoryProductDetails = (inventoryItem) => {
      const product = inventoryItem?.product || {};
      const price = inventoryItem?.price || {};
      const quantityUnit =
        price.price_unit_code ||
        product.quantity_unit ||
        product.default_quantity_unit ||
        "U";
      return {
        product,
        product_id: product.id,
        quantity: Number(inventoryItem?.quantity || 0),
        quantity_unit: quantityUnit,
        quantity_unit_label: price.price_unit_label || quantityUnit,
        price_unit_code: price.price_unit_code || quantityUnit,
        price_unit_label: price.price_unit_label || quantityUnit,
        price_unit_scope: price.price_unit_scope || "",
        price_base_quantity_unit:
          price.price_base_quantity_unit ||
          product.quantity_unit ||
          product.default_quantity_unit ||
          quantityUnit,
        price_conversion_factor_snapshot:
          price.price_conversion_factor_snapshot || undefined,
        unit_price: price.unit_price,
        raw: inventoryItem,
      };
    };

    const handleConfirmedSubmit = async () => {
      showConfirmation.value = false;
      formError.value = "";

      if (!canSubmit.value) {
        formError.value = "Ensure all items have valid quantities.";
        return;
      }

      saving.value = true;
      try {
        // CLEAR NAMING:
        // store_id = Requester (current store, wants items)
        // other_store_id = Fulfiller (selected store, has items)
        // product_id = Products from current store (requester)
        const payload = {
          store_id: props.targetStoreId, // Current store (requester)
          other_store_id: selectedTargetStoreId.value, // Selected store (fulfiller)
          items: requestItems.map((item) => ({
            product_id: item.product.id, // Products from current store
            product_mapping_id: item.productMappingId || undefined,
            quantity: Number(item.quantity),
            quantity_unit: item.quantityUnit,
            quantity_unit_ref: item.quantityUnitRef,
          })),
          note: note.value?.trim() || undefined,
        };

        console.log("📝 Submitting transfer request:");
        console.log("   Requester Store (Current):", props.targetStoreId);
        console.log(
          "   Fulfiller Store (Selected):",
          selectedTargetStoreId.value,
        );
        console.log("   Full Payload:", payload);

        // Use Vuex action
        const request = await store.dispatch(
          "transfers/createTransferRequest",
          {
            groupId: selectedGroupId.value,
            payload,
          },
        );

        console.log("✅ Transfer request created successfully");
        toast.success("Transfer request created successfully");
        emit("submitted", request || {});
      } catch (error) {
        console.error("❌ Failed to submit request:", error);
        formError.value =
          error?.response?.data?.error?.message ||
          "Unable to create request. Please try again.";
      } finally {
        saving.value = false;
      }
    };

    watch(
      () => props.isOpen,
      async (isOpen) => {
        console.log(`📋 Modal isOpen changed to: ${isOpen}`);
        if (isOpen) {
          resetState();
          await loadGroups();
          rebuildRequestItems();
        } else {
          resetState();
        }
      },
      { immediate: true },
    );

    watch(selectedGroupId, (newGroupId, oldGroupId) => {
      console.log(`🏷️ Group changed from ${oldGroupId} to ${newGroupId}`);
      if (newGroupId) {
        selectedTargetStoreId.value = "";
        targetStores.value = [];
        loadGroupStores(newGroupId);
      }
    });

    watch(selectedTargetStoreId, () => {
      refreshMappingAwareUnitOptions();
    });

    watch(
      () => props.selectedItems,
      () => {
        rebuildRequestItems();
      },
    );

    return {
      groups,
      groupSelectOptions,
      selectedGroupId,
      selectedTargetStoreId,
      note,
      targetStores,
      targetStoreSelectOptions,
      targetStoreName,
      requestItems,
      loading,
      loadingGroups,
      loadingStores,
      storesLoadError,
      saving,
      formError,
      showConfirmation,
      canSubmit,
      validItemsCount,
      quantityConversion,
      transferQuantitySummary,
      transferInventorySummary,
      loadGroups,
      loadGroupStores,
      handleUnitChange,
      handleConfirmedSubmit,
    };
  },
};
</script>

<style scoped>
/* ── Modal shell overrides ─────────────────────────────────────── */
:deep(.ireq-modal) {
  background:
    radial-gradient(circle at 92% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent), transparent 30%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 28px;
  box-shadow: 0 34px 100px rgba(15, 23, 42, 0.32);
  overflow: hidden;
}

:deep(.ireq-body) {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ── Flow container ───────────────────────────────────────────── */
.ireq-flow {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ── Hero / controls bar ──────────────────────────────────────── */
.ireq-hero {
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--hs-primary, #3293ec) 8%, transparent), transparent),
    color-mix(in srgb, var(--hs-bg, #f9fafb) 72%, var(--hs-surface, #ffffff));
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  flex-shrink: 0;
  padding: 20px 24px;
}

.ireq-steps {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.ireq-step {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.ireq-step-label {
  align-items: center;
  color: var(--hs-muted, #64748b);
  display: flex;
  font-size: 0.72rem;
  font-weight: 900;
  gap: 5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.ireq-required {
  color: #ef4444;
}

.ireq-optional {
  color: var(--hs-muted, #64748b);
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.ireq-row {
  display: flex;
  gap: 8px;
}

.ireq-select-wrap {
  flex: 1;
  min-width: 0;
}

.ireq-hs-select {
  width: 100%;
}

.ireq-icon-btn {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
  border-radius: 14px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;
  width: 40px;
}

.ireq-icon-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
  transform: translateY(-1px);
}

.ireq-icon-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.ireq-hint {
  align-items: center;
  color: var(--hs-muted, #64748b);
  display: flex;
  font-size: 0.75rem;
  gap: 5px;
  line-height: 1.4;
}

.ireq-hint--warn { color: #d97706; }
.ireq-hint--error { color: #dc2626; }

/* ── Content area ─────────────────────────────────────────────── */
.ireq-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

/* ── Empty / loading states ───────────────────────────────────── */
.ireq-empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}

.ireq-empty p {
  color: var(--hs-muted, #64748b);
  font-size: 0.92rem;
}

.ireq-empty-icon {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  border-radius: 999px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  height: 64px;
  justify-content: center;
  width: 64px;
}

/* ── Items list ───────────────────────────────────────────────── */
.ireq-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ireq-item-card {
  align-items: center;
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 16px 20px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
}

.ireq-item-card:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.1);
  transform: translateY(-1px);
}

.ireq-item-info {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 12px;
  min-width: 0;
}

.ireq-item-icon {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  border-radius: 12px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  flex-shrink: 0;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.ireq-item-meta {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.ireq-item-name {
  color: var(--hs-text, #0f172a);
  font-size: 0.92rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ireq-item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ireq-tag {
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  padding: 2px 8px;
  text-transform: uppercase;
}

.ireq-tag--sku {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 12%, transparent);
  color: var(--hs-muted, #64748b);
}

.ireq-tag--unit {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  color: var(--hs-primary, #3293ec);
}

.ireq-tag--mapped {
  background: color-mix(in srgb, #22c55e 14%, transparent);
  color: #16a34a;
}

.ireq-tag--unmapped {
  background: color-mix(in srgb, #f59e0b 14%, transparent);
  color: #b45309;
}

/* ── Quantity section ─────────────────────────────────────────── */
.ireq-item-qty {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 6px;
  width: 280px;
}

.ireq-qty-label {
  color: var(--hs-muted, #64748b);
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.ireq-qty-row {
  display: flex;
  gap: 8px;
}

.ireq-field-control {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 72%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  color: var(--hs-muted, #64748b);
  display: flex;
  gap: 8px;
  min-height: 42px;
  padding: 0 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.ireq-field-control:focus-within {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 48%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
}

.ireq-field-control--textarea {
  align-items: flex-start;
  padding-top: 12px;
}

.ireq-qty-input-wrap {
  width: 88px;
  flex-shrink: 0;
}

.ireq-unit-wrap {
  flex: 1;
  min-width: 0;
}

.ireq-qty-input,
.ireq-unit-select,
.ireq-note {
  background: transparent;
  border: 0;
  color: var(--hs-text, #0f172a);
  flex: 1;
  font-size: 0.88rem;
  min-width: 0;
  outline: none;
  padding: 10px 0;
  width: 100%;
}

.ireq-unit-select {
  cursor: pointer;
}

.ireq-unit-select:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.ireq-unit-conversion {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 7%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-muted, #64748b) 13%, transparent);
  border-radius: 12px;
  color: var(--hs-muted, #64748b);
  display: grid;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.35;
  padding: 8px 10px;
}

.ireq-unit-conversion--packaging {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 9%, transparent);
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
  color: color-mix(in srgb, var(--hs-primary, #3293ec) 82%, #0f172a);
}

.ireq-unit-conversion--inventory {
  background: color-mix(in srgb, #22c55e 8%, transparent);
  border-color: color-mix(in srgb, #22c55e 18%, transparent);
  color: #166534;
}

.ireq-unit-conversion__line {
  align-items: flex-start;
  display: flex;
  gap: 6px;
  min-width: 0;
}

.ireq-unit-conversion__line svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.ireq-note {
  min-height: 80px;
  padding: 8px 0;
  resize: vertical;
}

/* ── Note section ─────────────────────────────────────────────── */
.ireq-note-section {
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  padding-top: 18px;
}

.ireq-note-section--compact {
  border-top: 0;
  margin-top: 14px;
  padding-top: 0;
}

/* ── Spinner ──────────────────────────────────────────────────── */
.ireq-spinner {
  animation: ireq-spin 0.75s linear infinite;
  border: 2px solid color-mix(in srgb, var(--hs-primary, #3293ec) 24%, transparent);
  border-radius: 999px;
  border-top-color: var(--hs-primary, #3293ec);
  display: block;
  height: 20px;
  width: 20px;
}

.ireq-spinner--lg {
  height: 36px;
  width: 36px;
  border-width: 3px;
}

.ireq-spin {
  animation: ireq-spin 0.75s linear infinite;
}

@keyframes ireq-spin { to { transform: rotate(360deg); } }

/* ── Footer ───────────────────────────────────────────────────── */
.ireq-footer {
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--hs-primary, #3293ec) 8%, transparent), transparent),
    color-mix(in srgb, var(--hs-bg, #f9fafb) 72%, var(--hs-surface, #ffffff));
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  flex-shrink: 0;
  padding: 16px 24px;
}

.ireq-error-banner {
  align-items: center;
  background: color-mix(in srgb, #ef4444 10%, transparent);
  border: 1px solid color-mix(in srgb, #ef4444 24%, transparent);
  border-radius: 14px;
  color: #dc2626;
  display: flex;
  font-size: 0.84rem;
  font-weight: 600;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 14px;
}

.ireq-footer-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.ireq-counter {
  align-items: center;
  color: var(--hs-muted, #64748b);
  display: flex;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 6px;
}

.ireq-actions {
  align-items: center;
  display: flex;
  gap: 10px;
}

/* ── Buttons ──────────────────────────────────────────────────── */
.ireq-btn {
  align-items: center;
  border-radius: 14px;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 900;
  gap: 7px;
  min-height: 42px;
  padding: 0 18px;
  transition: box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.ireq-btn--cancel {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  color: var(--hs-text, #0f172a);
}

.ireq-btn--submit {
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), #2563eb);
  box-shadow: 0 10px 26px color-mix(in srgb, var(--hs-primary, #3293ec) 26%, transparent);
  color: white;
}

.ireq-btn:not(:disabled):hover {
  transform: translateY(-1px);
}

.ireq-btn--submit:not(:disabled):hover {
  box-shadow: 0 14px 34px color-mix(in srgb, var(--hs-primary, #3293ec) 34%, transparent);
}

.ireq-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.ireq-btn-spinner {
  animation: ireq-spin 0.75s linear infinite;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  border-top-color: white;
  display: block;
  flex-shrink: 0;
  height: 14px;
  width: 14px;
}

/* ── Confirmation overlay ─────────────────────────────────────── */
.ireq-confirm-overlay {
  align-items: center;
  backdrop-filter: blur(10px);
  background: rgba(15, 23, 42, 0.58);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 20px;
  position: fixed;
  z-index: 5600;
}

.ireq-confirm-panel {
  background:
    radial-gradient(circle at 88% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent), transparent 34%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.32);
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
  width: min(100%, 480px);
}

.ireq-confirm-icon {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  border-radius: 16px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  flex-shrink: 0;
  height: 52px;
  justify-content: center;
  margin: 24px 24px 0;
  width: 52px;
}

.ireq-confirm-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px 20px;
}

.ireq-confirm-eyebrow {
  color: var(--hs-primary, #3293ec);
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  margin: 0;
  text-transform: uppercase;
}

.ireq-confirm-title {
  color: var(--hs-text, #0f172a);
  font-size: 1.3rem;
  font-weight: 950;
  line-height: 1.2;
  margin: 0;
}

.ireq-confirm-desc {
  color: var(--hs-muted, #64748b);
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0;
}

.ireq-confirm-notice {
  align-items: flex-start;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 9%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
  border-radius: 12px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  font-size: 0.8rem;
  font-weight: 600;
  gap: 8px;
  line-height: 1.5;
  padding: 10px 12px;
}

.ireq-confirm-notice svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.ireq-confirm-items {
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: grid;
  max-height: 180px;
  overflow-y: auto;
}

.ireq-confirm-item {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 10px 12px;
}

.ireq-confirm-item + .ireq-confirm-item {
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.16));
}

.ireq-confirm-item span {
  color: var(--hs-text, #0f172a);
  font-size: 0.82rem;
  font-weight: 800;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ireq-confirm-item strong {
  color: var(--hs-primary, #3293ec);
  font-size: 0.78rem;
  font-weight: 950;
  text-align: right;
  white-space: nowrap;
}

.ireq-confirm-item strong small {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  margin-top: 2px;
}

.ireq-confirm-footer {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 66%, var(--hs-surface, #ffffff));
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 16px 24px;
}

/* ── Fade transition ──────────────────────────────────────────── */
.ireq-fade-enter-active,
.ireq-fade-leave-active {
  transition: opacity 0.18s ease;
}

.ireq-fade-enter-from,
.ireq-fade-leave-to {
  opacity: 0;
}

.ireq-fade-enter-active .ireq-confirm-panel,
.ireq-fade-leave-active .ireq-confirm-panel {
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.ireq-fade-enter-from .ireq-confirm-panel,
.ireq-fade-leave-to .ireq-confirm-panel {
  opacity: 0;
  transform: translateY(10px) scale(0.97);
}

/* ── Responsive ───────────────────────────────────────────────── */
@media (max-width: 760px) {
  .ireq-steps {
    grid-template-columns: 1fr;
  }

  .ireq-item-card {
    flex-direction: column;
    align-items: stretch;
  }

  .ireq-item-qty {
    width: 100%;
  }
}
</style>
